import React from 'react';
import { TableRow, TableCell, Chip, Typography, Box, Avatar, Tooltip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  CoordinatePatient, DISPOSITION_LABELS,
  STATUS_CHIP_STYLE, URGENCY_CHIP_STYLE,
} from '~/types/coordinate';

const CHIP_SX = { fontWeight: 600, fontSize: '0.68rem', height: 20 };

const CoordinatePatientRow: React.FC<{ patient: CoordinatePatient }> = ({ patient }) => {
  const navigate = useNavigate();
  const statusStyle = STATUS_CHIP_STYLE[patient.coordinationStatus];
  const urgencyStyle = URGENCY_CHIP_STYLE[patient.urgency];

  // Demo: deterministic floor/room/MRN from patient id, matching the Predict list pattern
  const hash = Array.from(patient.id).reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const floor = (hash % 8) + 2;
  const room = 100 + (hash % 40);
  const mrn = `MRN${String(100000 + (hash * 37) % 900000).slice(0, 6)}`;

  return (
    <TableRow
      hover
      onClick={() => navigate(`/coordinate/patient/${patient.id}`)}
      sx={{ cursor: 'pointer' }}
    >
      <TableCell>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500, lineHeight: 1.3 }}>
            {patient.name}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
            {mrn} • {patient.age} yrs
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>
          {DISPOSITION_LABELS[patient.disposition]}
        </Typography>
      </TableCell>
      <TableCell>
        <Box>
          <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{patient.facilityName}</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.4 }}>
            Floor {floor} · Rm {room}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ lineHeight: 1.4, whiteSpace: 'nowrap' }}>
          {(() => {
            // Demo: the mock admit dates anchor on 2026-05-01 which has drifted into the
            // past relative to actual today. Land the due date in [today, today + 4] using
            // a deterministic per-patient offset so the column always looks fresh.
            const offset = hash % 5;
            const due = new Date();
            due.setDate(due.getDate() + offset);
            return due.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
          })()}
        </Typography>
      </TableCell>
      <TableCell>
        <Typography variant="body2" sx={{ lineHeight: 1.4 }}>{patient.insurance}</Typography>
      </TableCell>
      <TableCell>
        <Chip
          label={statusStyle.label}
          size="small"
          sx={{ ...CHIP_SX, bgcolor: statusStyle.bgcolor, color: statusStyle.color }}
        />
      </TableCell>
      <TableCell>
        <Chip
          label={urgencyStyle.label}
          size="small"
          sx={{ ...CHIP_SX, bgcolor: urgencyStyle.bgcolor, color: urgencyStyle.color }}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Tooltip title={patient.assignedTo} arrow>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                fontSize: '0.875rem',
                bgcolor: 'primary.main',
              }}
            >
              {patient.assignedTo
                .split(/\s+/)
                .map(p => p[0])
                .filter(Boolean)
                .slice(0, 2)
                .join('')
                .toUpperCase()}
            </Avatar>
          </Tooltip>
        </Box>
      </TableCell>
    </TableRow>
  );
};

export default CoordinatePatientRow;
